import handleClickAwayLanguageSelection from '../../utils/handleClickAwayLanguageSelection';
import LanguageSelectionForm from '../../../common/components/LanguageSelectionForm';
import React, { useEffect, useState } from 'react';
import { AssessmentDetails, LanguageDetail, TaskListConfiguration, TaskTileProps } from '../../interfaces';
import { createLaunchConfig } from '../../../common/utils/launchConfig';
import { getFormattedScheduledTaskDate } from '../../../common/utils/dateFormatter';
import { Language } from '../../../common/interfaces/language';
import { TaskItemStatus, taskListDefaultConfiguration } from '../../constants';
import { TaskTileStyles } from '../../styles';
import { useTranslation } from 'react-i18next';
import { WorkflowItemAssessmentCodes, WorkflowItemCode } from '../../../constants';
import {
  Box,
  CardActionArea,
  CardHeader,
  LinearProgress,
  Typography,
  ClickAwayListener,
  Grid,
} from '@material-ui/core';

import { ReactComponent as LockTileIcon } from '../../images/lock.svg';
import { ReactComponent as CompletedTaskIcon } from '../../images/completed_task.svg';
import { ReactComponent as InProgressTaskIcon } from '../../images/in_progress.svg';
import { initializeTask } from '../../utils/initializeTask';
import { useHistory, useLocation } from 'react-router-dom';
import { useBasicDetails } from '../../../store';
import { useScheduledAssessmentTimerLayout02 } from '../../hooks/useScheduledAssessmentTimerLayout02';
import localizeContentMapper from '../../../common/utils/localizeContentMapper';
import { TaskListToDoStyles } from './styles/taskList';

interface LockTileHOCProps {
  children?: React.ReactNode;
  className?: string;
}

/** Task Tile Component */
const TaskTile = (props: TaskTileProps): JSX.Element => {
  const {
    isTaskLocked,
    rowContainsScheduledAssessment,
    task,
    isSequentiallyLocked,
    fetchAssessmentLaunguages,
    isFirstTask,
    sequential,
  } = props;
  const {
    //template: { settings },
    currentWorkflowItem: { code, configuration, localizedContentMap },
  } = useBasicDetails();

  const parsedConfig =
    configuration && code === WorkflowItemCode.TASKLIST
      ? JSON.parse(configuration).configWidgetJSON
      : taskListDefaultConfiguration;
  const taskListConfiguration: TaskListConfiguration = localizeContentMapper(parsedConfig, localizedContentMap);

  const { title, subtitle, duration, items, status, backgroundColor } = task;
  const history = useHistory();
  const location = useLocation();
  const { userSessionActive } = useBasicDetails();
  const { t } = useTranslation();
  const taskTileStyles = TaskTileStyles(props);
  const taskListStyles = TaskListToDoStyles(taskListConfiguration.styles);

  const [isLanguageSelectionFormOpen, setLanguageSelectionFormOpen] = useState(false);
  const [assessmentDetail, setAssessmentDetail] = useState<AssessmentDetails>({
    availableLanguages: [],
    activeComponentId: 0,
    projectId: 0,
    packageId: 0,
    displayOptions: [],
    selectedLanguageCode: 'EN_US',
    sittingId: 0,
    defaultPackageLanguage: {
      testId: 0,
      language: {
        default: false,
        description: '',
        id: 0,
        integrationIsoCode: '',
        isoCode: '',
        name: '',
      },
    },
  });
  const [isLoadingAssessmentLanguages, setLoadingAssessmentLanguages] = useState(false);
  const scheduleableTask = task.items.filter(
    (item) => item.code === WorkflowItemCode.EXPASSES && item.isSchedulable && item.scheduled,
  );

  const schedulableAssessment = useScheduledAssessmentTimerLayout02(scheduleableTask[0]);

  /**
   * isLocked - Cases for schedulable
   * Case 1 - For sequential, lock task with first item gencontent untill it is sequentially active
   *              isSequentiallyLocked && task.items[0].code !== WorkflowItemCode.EXPASSES
   * Case 2 - For non sequential, unlock task with first item gencontent
   * Case 3 - For non sequential, lock task with first item assessment
   * Case 4 - For sequential, lock task with first item assessment
   *              (task.items[0].code === WorkflowItemCode.EXPASSES && schedulableAssessment.disabled)
   */
  const isLocked = task.isSchedulable
    ? (isSequentiallyLocked && task.items[0].code !== WorkflowItemCode.EXPASSES && schedulableAssessment.disabled) ||
      (task.items[0].code === WorkflowItemCode.EXPASSES && schedulableAssessment.disabled) ||
      task.status === TaskItemStatus.COMPLETED
    : isTaskLocked;

  //const isVideoTask = task.items.some((item) => item.code === WorkflowItemCode.EXPVIDEOTASK);
  const handleTaskTileClick = async (): Promise<void> => {
    const firstNotStartedOrInProgressItem = items.find(
      (item) =>
        item.status === TaskItemStatus.IN_PROGRESS || !item.status || item.status === TaskItemStatus.NOT_STARTED,
    );
    const currentItem = firstNotStartedOrInProgressItem ? firstNotStartedOrInProgressItem : items[0];
    setLoadingAssessmentLanguages(true);
    if (WorkflowItemAssessmentCodes.includes(currentItem.code) && !currentItem.state) {
      if (!currentItem.isSchedulable) {
        setLanguageSelectionFormOpen(true);
      }

      const { data } = await fetchAssessmentLaunguages(currentItem);
      if (data && data.availableLanguages) {
        data.availableLanguages = data.availableLanguages.map((lang: LanguageDetail) => ({
          testId: lang.testId,
          activeComponentId: lang.activeComponentId,
          ...lang.language,
        }));
        setAssessmentDetail(data);

        if (data.availableLanguages.length === 1) {
          setLanguageSelectionFormOpen(false);
        }
      }
    } else {
      await initializeTask(task, userSessionActive, history, location);
    }

    setLoadingAssessmentLanguages(false);
  };

  const onLanguageSubmit = async (selectedLanguage: Language): Promise<void> => {
    const firstNotStartedOrInProgressItem = task.items.find(
      (item) => !item.status || item.status === TaskItemStatus.IN_PROGRESS,
    );
    const currentItem = firstNotStartedOrInProgressItem ? firstNotStartedOrInProgressItem : task.items[0];
    // creating launch config
    const launchAssessmentConfig = createLaunchConfig(currentItem.id, task.id, selectedLanguage, assessmentDetail);
    await initializeTask(task, userSessionActive, history, location, launchAssessmentConfig);
  };

  // For forced langauge, directly launch the assessment on tile click
  useEffect(() => {
    if (!isLoadingAssessmentLanguages && assessmentDetail.availableLanguages.length === 1) {
      onLanguageSubmit(assessmentDetail.availableLanguages[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingAssessmentLanguages, assessmentDetail]);
  const [mouseHoveredIn, setMouseHoveredIn] = useState(false);
  const shouldShowStartButton = (): boolean => {
    //If the task is locked or have any defined status(either started,in-progress etc.) then do not show start
    if (
      sequential &&
      (isLocked || (status && status !== TaskItemStatus.NOT_STARTED) || (task.optional && !isFirstTask)) //For sequential task
    )
      return false;
    //If the task has no lock i.e not sequential then show start on first task/mouse hovered over the task
    else if (!sequential && (!status || status === TaskItemStatus.NOT_STARTED)) {
      if (isFirstTask || mouseHoveredIn) return true;
      else return false;
    } else if (!isSequentiallyLocked && status && status !== TaskItemStatus.NOT_STARTED) return false;
    //For both seq/non-seq and not locked
    else return true; //else show start button
  };
  const Duration = ({ duration }: { duration: number }): JSX.Element => {
    return (
      <span className={`${taskTileStyles.timerText} ${taskListStyles.timerText}`}>{`${duration} ${t(
        'minuteAbbreviation',
      )}`}</span>
    );
  };
  const scheduledTime = (): JSX.Element => {
    //The time that will be shown for scheduled task start time
    if (task.optional) {
      return <></>;
    }
    const date = scheduleableTask[0].startTime;
    const startTime = getFormattedScheduledTaskDate(parseInt(date ? date.replace('/Date(', '') : '', 10));
    return (
      <Box component="span" pl={1}>
        {startTime}
      </Box>
    );
  };
  const LockTileHOC = ({ className, children }: LockTileHOCProps): JSX.Element => {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        className={`${className || ''} ${taskTileStyles.IELockFix}`}
        title={!isLocked ? t('taskTileOpenStatus') : ''}
        aria-label={!isLocked ? t('taskTileOpenStatus') : ''}>
        {task.isSchedulable && !schedulableAssessment.disabled && task.status !== TaskItemStatus.COMPLETED && (
          <Box className={taskListStyles.startButton}>
            <span className={`${taskListStyles.timerText}`}>
              {`${schedulableAssessment.message}`}
              {/\d/.test(schedulableAssessment.message)
                ? ` ${t('minuteAbbreviation')}`
                : duration && <Duration duration={duration}></Duration>}
            </span>
          </Box>
          /*if message contains digit then the message is like 'Starts in 14:00' so duration is should not be there*/
        )}
        {isLocked && ( //the lock icon in the start of task tile
          <>
            <Box
              className={taskListStyles.lock}
              title={t('taskTileLockedStatus')}
              aria-label={t('taskTileLockedStatus')}>
              <LockTileIcon width="77px" height="77px" />
            </Box>
            {status !== TaskItemStatus.COMPLETED && duration && <Duration duration={duration}></Duration>}
          </>
        )}
        {!isLocked && task.isSchedulable && schedulableAssessment.disabled && duration && (
          <Duration duration={duration}></Duration>
        )}

        {!isLocked && !task.isSchedulable && (
          <Box
            className={shouldShowStartButton() ? `${taskListStyles.startButton}` : `${taskListStyles.taskIconButton}`}>
            {shouldShowStartButton() ? <StartButton /> : <TaskIcon />}
          </Box>
        )}
        {children}
      </Box>
    );
  };

  const TaskIcon = (): JSX.Element => {
    //This is a task icon that will be shown below lock icon in vertical layout
    if (!status) {
      if (duration) {
        return <Duration duration={duration} />;
      } else return <>{''}</>;
    } else if (status === TaskItemStatus.IN_PROGRESS || status === TaskItemStatus.EVALUATION_PENDING) {
      return (
        <InProgressTaskIcon
          className={taskListStyles.taskIcon}
          title={t('taskTileInProgressStatus')}
          aria-label={t('taskTileInProgressStatus')}
        />
      );
    } else if (status === TaskItemStatus.COMPLETED) {
      return (
        <CompletedTaskIcon
          className={taskListStyles.taskIcon}
          title={t('taskTileCompletedStatus')}
          aria-label={t('taskTileCompletedStatus')}
        />
      );
    }

    return <>{''}</>;
  };

  const StartButton = (): JSX.Element => {
    return (
      <>
        <span>{taskListConfiguration.styles.tiles.startButtonText}</span>
        {duration && <Duration duration={duration}></Duration>}
      </>
    );
  };

  return (
    <Box className={`${taskTileStyles.tileActionArea} ${taskTileStyles.tile}`}>
      <CardActionArea
        component="div"
        className={` ${taskTileStyles.tileCard} ${taskListStyles.tileCard}`}
        style={{ backgroundColor: backgroundColor ? backgroundColor : '' }}
        onClick={handleTaskTileClick}
        disableRipple={isLanguageSelectionFormOpen}
        disabled={isLocked || isLoadingAssessmentLanguages}
        onMouseEnter={(): void => {
          setMouseHoveredIn(true);
        }}
        onMouseLeave={(): void => {
          setMouseHoveredIn(false);
        }}>
        <Box>
          <Grid container>
            <Grid item xs={2} className={`${taskListStyles.actionIconBorder}`}>
              {<LockTileHOC className={taskListStyles.tileHeader} />}
            </Grid>
            <Grid item xs={10}>
              <Box className={`${taskListStyles.timerheader}`}>
                <CardHeader
                  className={`${taskTileStyles.actionIcon}`}
                  action={
                    <>
                      {task.optional && (
                        <Typography
                          className={`${taskListStyles.optionalTask} ${taskTileStyles.optionalTask}`}
                          variant="body2"
                          color="inherit">
                          {t('optional')}
                        </Typography>
                      )}
                    </>
                  }
                  title={
                    //task title
                    <Typography variant="subtitle1" color="inherit">
                      <strong>{title}</strong>
                    </Typography>
                  }
                  subheader={
                    //task subtitile
                    <Typography variant="subtitle2" color="inherit">
                      {subtitle}
                    </Typography>
                  }
                />
                {task.isSchedulable && schedulableAssessment.disabled
                  ? scheduledTime()
                  : rowContainsScheduledAssessment && <Box height={44}></Box>}
              </Box>
            </Grid>
          </Grid>
        </Box>
        {isLanguageSelectionFormOpen && (
          <Box className={taskTileStyles.languageSelectionOverlay} onClick={(ev): void => ev.stopPropagation()}>
            <ClickAwayListener
              onClickAway={(e: React.MouseEvent<Document, MouseEvent>): void => {
                handleClickAwayLanguageSelection(e, setLanguageSelectionFormOpen);
              }}>
              <Box className={taskTileStyles.languageSelectionModal}>
                {isLoadingAssessmentLanguages ? (
                  <LinearProgress color="primary" />
                ) : (
                  <LanguageSelectionForm
                    languages={assessmentDetail.availableLanguages}
                    onLanguageSubmit={onLanguageSubmit}
                  />
                )}
              </Box>
            </ClickAwayListener>
          </Box>
        )}
      </CardActionArea>
    </Box>
  );
};

export default TaskTile;
